<script lang="ts">
  import { onMount } from 'svelte';
  import { Bell, Moon, Sun, User, CreditCard, Globe, Trash2, AlertTriangle, Sparkles, Volume2, Database, GraduationCap } from 'lucide-svelte';
  import type { ProficiencyLevel } from '../stores/settings';
  import Card from './ui/card.svelte';
  import Button from './ui/button.svelte';
  import SyncStatus from './SyncStatus.svelte';
  import UserProfileDialog from './dialogs/UserProfileDialog.svelte';
  import DeleteAccountDialog from './dialogs/DeleteAccountDialog.svelte';
  import SelectLanguageDialog from './dialogs/SelectLanguageDialog.svelte';
  import { userSettings, isLoadingSettings, settingsActions } from '../stores/settings';
  import { authActions, user } from '../stores/auth';
  import { navigationActions } from '../stores/navigation';
  import { targetLanguages, selectedTargetLanguage, flashcardActions } from '../stores/flashcards';
  import TargetLanguageDialog from './dialogs/TargetLanguageDialog.svelte';
  import { _ } from 'svelte-i18n';
  import { clearTTSCache, getTTSCacheStats, formatCacheSize } from '../lib/tts-cache';

  let showProfileModal = $state(false);
  let showDeleteAccountDialog = $state(false);
  let showLanguageModal = $state(false);
  let showTargetLanguageModal = $state(false);
  let deletionReason = $state('');
  let backupRequested = $state(true);
  let isDeletingAccount = $state(false);
  let deleteError = $state<string | null>(null);
  
  // TTS cache state
  let cacheStats = $state<{ count: number; totalSize: number }>({ count: 0, totalSize: 0 });
  let isClearingCache = $state(false);
  
  async function loadCacheStats() {
    cacheStats = await getTTSCacheStats();
  }
  
  async function handleClearCache() {
    isClearingCache = true;
    await clearTTSCache();
    cacheStats = { count: 0, totalSize: 0 };
    isClearingCache = false;
  }

  // Load TTS cache stats on mount
  onMount(() => {
    loadCacheStats();
  });

  const toggleTheme = async () => {
    await settingsActions.toggleTheme();
  };

  const toggleNotifications = async () => {
    await settingsActions.toggleSetting('notifications_enabled');
  };

  const updateReminderTime = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    await settingsActions.updateSetting('daily_reminder_time', input.value);
  };

  const toggleAutoPlay = async () => {
    await settingsActions.toggleSetting('auto_play_audio');
  };

  const upgradeToPro = async () => {
    await settingsActions.updateSetting('pro_status', true);
  };

  const handleLogout = async () => {
    const success = await authActions.signOut();
    if (success) {
      // Clear any local state if needed
      navigationActions.goHome();
    }
  };

  const handleEditProfile = () => {
    showProfileModal = true;
  };

  const handleDeleteAccountClick = () => {
    showDeleteAccountDialog = true;
    deleteError = null;
  };

  const handleDeleteAccountConfirm = async () => {
    isDeletingAccount = true;
    deleteError = null;

    const result = await authActions.deleteAccount(deletionReason || undefined, backupRequested);

    if (result.success) {
      // User is automatically signed out in deleteAccount function
      navigationActions.goHome();
    } else {
      deleteError = result.error || $_('settings.danger.error');
      isDeletingAccount = false;
    }
  };

  const handleDeleteAccountCancel = () => {
    showDeleteAccountDialog = false;
    deletionReason = '';
    backupRequested = true;
    deleteError = null;
  };

  const handleDeleteAccountDialogConfirm = async (reason: string, backup: boolean) => {
    isDeletingAccount = true;
    deleteError = null;

    const result = await authActions.deleteAccount(reason || undefined, backup);

    if (result.success) {
      navigationActions.goHome();
    } else {
      deleteError = result.error || $_('settings.danger.error');
      isDeletingAccount = false;
    }
  };

  const languageOptions = [
    { value: 'cs', labelKey: 'language.names.cs', flag: '🇨🇿' },
    { value: 'ar', labelKey: 'language.names.ar', flag: '🇸🇦' },
    { value: 'en', labelKey: 'language.names.en', flag: '🇬🇧' },
  ];

  const proficiencyLevels: { value: ProficiencyLevel; labelKey: string; description: string }[] = [
    { value: 'A1', labelKey: 'settings.proficiency.a1', description: 'Beginner' },
    { value: 'A2', labelKey: 'settings.proficiency.a2', description: 'Elementary' },
    { value: 'B1', labelKey: 'settings.proficiency.b1', description: 'Intermediate' },
    { value: 'B2', labelKey: 'settings.proficiency.b2', description: 'Upper Intermediate' },
    { value: 'C1', labelKey: 'settings.proficiency.c1', description: 'Advanced' },
    { value: 'C2', labelKey: 'settings.proficiency.c2', description: 'Proficient' },
  ];

</script>

<div class="min-h-screen bg-background pb-28">
  <div class="container mx-auto px-4 py-6 max-w-md">

    <!-- User Profile Widget -->
    <Card class="mb-6 widget-card tone-header p-6">
      <div class="mb-5 flex items-start justify-between gap-4">
        <div>
          <p class="section-label">{$_('settings.profile.title')}</p>
          <button
            type="button"
            class="flex mt-2 items-center gap-3 font-semibold leading-tight text-foreground transition hover:text-foreground/90 focus:outline-none"
            onclick={handleEditProfile}
          >
            <div class="h-12 w-12 rounded-full bg-foreground/10 shadow-soft-sm overflow-hidden flex items-center justify-center">
              {#if $user?.user_metadata?.avatar_url}
                <img
                  src={$user.user_metadata.avatar_url}
                  alt="Avatar"
                  class="h-full w-full object-cover"
                  loading="lazy"
                />
              {:else}
                <User class="w-6 h-6 text-foreground/60" />
              {/if}
            </div>
            <div class="text-left">
              <p class="text-xl font-semibold text-foreground">
                @{$user?.user_metadata?.nickname || $user?.email?.split('@')[0] || $_('settings.profile.anonymous')}
              </p>
            </div>
          </button>
        </div>
        <div class="rounded-2xl widget-muted px-4 py-3 text-right">
          <p class="section-label text-[0.68rem] tracking-[0.24em]">{$_('settings.profile.account_type')}</p>
          <p class="text-lg font-semibold text-foreground mt-1">
            {$userSettings?.pro_status ? 'PRO' : 'FREE'}
          </p>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="flex gap-2">
        <Button onclick={handleEditProfile} class="flex-1 btn btn-action" variant="outline" size="sm">
          {$_('settings.profile.edit')}
        </Button>
        <Button onclick={handleLogout} class="flex-1 btn btn-action" variant="outline" size="sm">
          {$_('settings.profile.sign_out')}
        </Button>
      </div>
    </Card>

    <h2 class="section-title">{$_('settings.language.section_title')}</h2>

    <Card class="mb-4 widget-card tone-purple p-4">
      <!-- Language Preference -->
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-3">
          <Globe class="w-5 h-5 text-primary" />
          <div>
            <div class="font-semibold">{$_('settings.language.primary_label')}</div>
          </div>
        </div>
      </div>
      <button
        type="button"
        onclick={() => showLanguageModal = true}
        class="btn btn-action w-full justify-start"
      >
        <div class="h-6 w-9 rounded overflow-hidden shadow-soft-sm flex-shrink-0">
          <img
            src={`https://flagcdn.com/${languageOptions.find(l => l.value === ($userSettings?.preferred_language || 'cs'))?.value === 'en' ? 'gb' : languageOptions.find(l => l.value === ($userSettings?.preferred_language || 'cs'))?.value === 'ar' ? 'sa' : 'cz'}.svg`}
            alt={$_(languageOptions.find(l => l.value === ($userSettings?.preferred_language || 'cs'))?.labelKey ?? 'language.names.cs')}
            class="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
        <span class="flex-1 font-medium">
          {$_(languageOptions.find(l => l.value === ($userSettings?.preferred_language || 'cs'))?.labelKey ?? 'language.names.cs')}
        </span>
        <svg class="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <!-- Target Language -->
      <div class="flex items-center justify-between mt-4">
        <div class="flex items-center gap-3">
          <Sparkles class="w-5 h-5 text-primary" />
          <div>
            <div class="font-semibold">{$_('settings.language.target_label')}</div>
          </div>
        </div>
      </div>
      <button
        type="button"
        onclick={() => showTargetLanguageModal = true}
        class="btn btn-action w-full justify-start mt-2"
      >
        <div class="h-6 w-9 rounded overflow-hidden shadow-soft-sm flex-shrink-0">
          {#if $selectedTargetLanguage}
            <img
              src={`https://flagcdn.com/${(() => {
                const code = $selectedTargetLanguage?.code?.toLowerCase() ?? '';
                const flagMap: Record<string, string> = { en: 'gb', es: 'es', fr: 'fr', de: 'de', it: 'it', pt: 'pt', ru: 'ru', zh: 'cn', ja: 'jp', ko: 'kr', ar: 'sa', cs: 'cz', pl: 'pl', tr: 'tr', uk: 'ua', th: 'th', vi: 'vn', id: 'id', hi: 'in' };
                return flagMap[code] ?? code;
              })()}.svg`}
              alt={$selectedTargetLanguage?.name}
              class="h-full w-full object-cover"
              loading="lazy"
            />
          {/if}
        </div>
        <span class="flex-1 font-medium">
          {$selectedTargetLanguage?.name || $_('language.select_language')}
        </span>
        <svg class="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <!-- Proficiency Level -->
      <div class="flex items-center justify-between mt-4">
        <div class="flex items-center gap-3">
          <GraduationCap class="w-5 h-5 text-primary" />
          <div>
            <div class="font-semibold">{$_('settings.proficiency.label')}</div>
            <div class="text-sm text-muted-foreground">{$_('settings.proficiency.note')}</div>
          </div>
        </div>
      </div>
      <div class="mt-2">
        <select
          class="form-select w-full text-sm px-3 py-2 rounded-lg border border-border bg-background"
          value={$userSettings?.proficiency_level || 'A1'}
          onchange={(e) => settingsActions.updateSetting('proficiency_level', e.currentTarget.value as ProficiencyLevel)}
        >
          {#each proficiencyLevels as level}
            <option value={level.value}>{$_(level.labelKey)}</option>
          {/each}
        </select>
      </div>
      </Card>

      <!-- Theme -->
    <h2 class="section-title">{$_('settings.environment.section_title')}</h2>

      <Card class="mb-4 widget-card tone-purple p-4">

      <div class="flex items-center justify-between mb-3">

        <div class="flex items-center gap-3 mb-2">
          <Sun class="w-5 h-5 text-warning" />
          <div>
            <div class="font-semibold">{$_('settings.environment.theme_label')}</div>
            <div class="text-sm text-muted-foreground">{$_('settings.environment.theme_note')}</div>
          </div>
        </div>
        <div>
          <Button onclick={toggleTheme} variant="outline" class="btn btn-action">
            {#if $userSettings?.theme === 'dark'}
              <Moon class="w-4 h-4 mr-2" /> {$_('settings.environment.dark')}
            {:else}
              <Sun class="w-4 h-4 mr-2" /> {$_('settings.environment.light')}
            {/if}
          </Button>
        </div>
      </div>

      <div class="flex items-center justify-between mt-3 mb-3">
        <div class="flex items-center gap-3">
          <Bell class="w-5 h-5 text-primary" />
          <div>
            <div class="font-semibold">{$_('settings.environment.notifications_label')}</div>
            <div class="text-sm text-muted-foreground">{$_('settings.environment.notifications_note')}</div>
          </div>
        </div>
        <div>
          <Button onclick={toggleNotifications} variant="outline" class="btn btn-action">
            {#if $userSettings?.notifications_enabled}
              {$_('common.enabled')}
            {:else}
              {$_('common.disabled')}
            {/if}
          </Button>
        </div>
      </div>

      {#if $userSettings?.notifications_enabled}
        <div class="mt-3 ml-8 pl-3 border-l-2 border-muted mb-3">
          <label for="reminder-time" class="form-label">
            {$_('settings.environment.reminder_label')}
          </label>
          <input
            id="reminder-time"
            type="time"
            value={$userSettings?.daily_reminder_time || '20:00'}
            onchange={updateReminderTime}
            class="form-input"
          />
          <p class="text-xs text-muted-foreground mt-1">
            {$_('settings.environment.reminder_note')}
          </p>
        </div>
      {/if}

      <div class="flex items-center justify-between mt-3 mb-3">
        <div class="flex items-center gap-3">
          <Moon class="w-5 h-5 text-muted-foreground" />
          <div>
            <div class="font-semibold">{$_('settings.environment.autoplay_label')}</div>
            <div class="text-sm text-muted-foreground">{$_('settings.environment.autoplay_note')}</div>
          </div>
        </div>
        <div>
          <Button onclick={toggleAutoPlay} variant="outline" class="btn btn-action">
            {#if $userSettings?.auto_play_audio}
              {$_('common.enabled')}
            {:else}
              {$_('common.disabled')}
            {/if}
          </Button>
        </div>
      </div>

      <div class="flex items-center justify-between mt-3 mb-3">
        <div class="flex items-center gap-3">
          <Globe class="w-5 h-5 text-primary" />
          <div>
            <div class="font-semibold">{$_('settings.environment.phonetic_label')}</div>
            <div class="text-sm text-muted-foreground">{$_('settings.environment.phonetic_note')}</div>
          </div>
        </div>
        <div>
          <Button onclick={() => settingsActions.toggleSetting('show_ipa_phonetic')} variant="outline" class="btn btn-action">
            {#if $userSettings?.show_ipa_phonetic}
              IPA
            {:else}
              {$_('settings.environment.transliteration')}
            {/if}
          </Button>
        </div>
      </div>

      <div class="flex items-center justify-between mt-3 mb-3">
        <div class="flex items-center gap-3">
          <Volume2 class="w-5 h-5 text-primary" />
          <div>
            <div class="font-semibold">{$_('settings.environment.tts_voice_label')}</div>
            <div class="text-sm text-muted-foreground">{$_('settings.environment.tts_voice_note')}</div>
          </div>
        </div>
        <div>
          <select
            class="form-select text-sm px-3 py-2 rounded-lg border border-border bg-background"
            value={$userSettings?.elevenlabs_voice || 'brian'}
            onchange={(e) => settingsActions.updateSetting('elevenlabs_voice', e.currentTarget.value as 'brian' | 'alice')}
          >
            <option value="brian">{$_('settings.environment.voice_brian')}</option>
            <option value="alice">{$_('settings.environment.voice_alice')}</option>
          </select>
        </div>
      </div>

      <div class="flex items-center justify-between mt-3 mb-3">
        <div class="flex items-center gap-3">
          <Database class="w-5 h-5 text-muted-foreground" />
          <div>
            <div class="font-semibold">{$_('settings.environment.audio_cache_label')}</div>
            <div class="text-sm text-muted-foreground">
              {$_('settings.environment.audio_cache_note')}
            </div>
            {#if cacheStats.count > 0}
              <div class="text-xs text-primary mt-1">
                {cacheStats.count} {$_('settings.environment.audio_cache_items')} • {formatCacheSize(cacheStats.totalSize)}
              </div>
            {/if}
          </div>
        </div>
        <div>
          <Button 
            onclick={handleClearCache} 
            variant="outline" 
            class="btn btn-action"
            disabled={isClearingCache || cacheStats.count === 0}
          >
            {#if isClearingCache}
              ...
            {:else}
              {$_('settings.environment.clear_cache')}
            {/if}
          </Button>
        </div>
      </div>
    </Card>

    <h2 class="section-title">{$_('settings.sync.title')}</h2>

    <Card class="mb-4 widget-card tone-purple p-4">
      <div class="mb-3">
        <div class="text-sm text-muted-foreground mb-4">
          {$_('settings.sync.description')}
        </div>
      </div>
      <SyncStatus />
    </Card>

    <!-- Delete Account Section -->
    <h2 class="section-title">{$_('settings.danger.title')}</h2>

    <Card class="mt-6 widget-card tone-red p-4">
      <div class="flex items-center gap-3 mb-3">
        <Trash2 class="w-6 h-6 text-destructive" />
        <div>
          <div class="text-sm text-muted-foreground">{$_('settings.danger.delete_warning')}</div>
        </div>
      </div>
      <Button onclick={handleDeleteAccountClick} variant="outline" class="btn btn-action w-full tone-red">
        <Trash2 class="w-4 h-4 mr-2" />
        {$_('settings.danger.delete_account')}
      </Button>
    </Card>
  </div>
</div>

{#if showProfileModal}
  <UserProfileDialog open={showProfileModal} onClose={() => showProfileModal = false} />
{/if}

{#if showLanguageModal}
  <SelectLanguageDialog open={showLanguageModal} onClose={() => showLanguageModal = false} />
{/if}

<TargetLanguageDialog isOpen={showTargetLanguageModal} onClose={() => (showTargetLanguageModal = false)} />

<DeleteAccountDialog 
  open={showDeleteAccountDialog}
  onClose={handleDeleteAccountCancel}
  onConfirm={handleDeleteAccountDialogConfirm}
  isDeleting={isDeletingAccount}
  error={deleteError}
/>

<style>
  @keyframes scale-in {
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
</style>
